import React, {
  ComponentProps,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { QueryClient } from '@tanstack/react-query';
import type { Request } from 'express';

import { Box } from '@rover/kibble/core';
import useLocationContext from '@rover/react-lib/src/context/useLocationContext';
import getQueryError from '@rover/react-lib/src/utils/getQueryError';
import {
  apiTrainingSearchRetrieve,
  getApiTrainingSearchRetrieveQueryKey,
  useApiTrainingSearchRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-training-search-retrieve';
import { TrainingSearchResponse } from '@rover/rsdk/src/apiClient/latest/model/trainingSearchResponse';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import emitMetric from '@rover/utilities/emitMetric';

import FloatingFooter from '../../../components/utils/FloatingFooter';
import { FullPageLoading } from '../components';
import TrainingFaqs from '../components/FrequentlyAskedQuestions/TrainingFaqs';
import {
  TrainerSearchBackToTopButtonClicked,
  TrainerSearchFaqPreviewReadMoreClicked,
  TrainerSearchHeaderLearnMoreClicked,
  TrainerSearchPageResults,
  TrainingSearchServiceInterestLoad,
} from '../events/analyticsEvents';
import {
  decodeSkillAndBehaviorQueryParams,
  SkillsAndBehaviorSlugsDictionary,
} from '../utils/params';

import {
  BACK_TO_TOP_STARTING_ELEMENT_ID,
  BackToTopButton,
  useBackToTopStartOffset,
} from './components/BackToTopButton';
import FilterSection from './components/FilterSection';
import MarketingContextHeader from './components/MarketingContextHeader';
import SearchResultSection from './components/SearchResultSection';
import ServiceInterestModal from './components/ServiceInterestModal/ServiceInterestModal';
import TrainerSearchFooter from './components/TrainerSearchFooter';
import { useScrollToComponent } from './hooks/useScrollToComponent';
import { DesktopFaqSection, FaqSection, TrainerSearchPageLayout } from './layouts';
import {
  hasUtmParams,
  parseServerQueryParamsForApi,
  parseUrlQueryParamsForApi,
  syncUrlQueryParams,
  TrainerSearchPageApiQueryParams,
} from './params';
import { getGroupedResults, GroupedResults } from './utilities';

type SearchResultsAnalytics = TrainingSearchResponse & {
  skillsAndBehaviors: {
    skills: string[];
    behaviors: string[];
  };
  usedAutoLocation: boolean;
};

function emitSearchResultsAnalyticsEvent(data: SearchResultsAnalytics): void {
  const virtualCount = data.results.filter((result) => result.virtualOnly).length;
  const analyticsData = {
    resultCount: data.results.length,
    virtualCount,
    searchQueryLocation: data.query.location,
    skillsAndBehaviors: data.skillsAndBehaviors,
    usedAutoLocation: data.usedAutoLocation,
    ...(data.analytics || {}),
  };
  emitAnalyticsEvent(new TrainerSearchPageResults(analyticsData));
}

const TrainerSearchPageHeader = (): JSX.Element => (
  <Helmet>
    {/* (DEV-104195): Translate */}
    <title>Find Dog Training Near You | Rover</title>
    {/* (DEV-104195): Translate */}
    <meta
      name="description"
      content="Discover and book private dog trainers for leash, potty, crate training, and more. Read verified reviews on trusted dog trainers in your neighborhood."
    />
  </Helmet>
);

function TrainerSearchPage(): ReactNode {
  const { componentRef: faqRef, scrollToComponent: scrollToFaq } = useScrollToComponent();
  const { componentRef: topOfPageRef, scrollToComponent: scrollToTopOfPage } =
    useScrollToComponent();

  const backToTopStartOffset = useBackToTopStartOffset();

  const { search: initialQueryFromUrl } = useLocationContext();
  const shouldDisplayMarketingContextHeader = useMemo(() => {
    return hasUtmParams(initialQueryFromUrl);
  }, [initialQueryFromUrl]);

  const [searchQueryForApi, setSearchQueryForApi] = useState<TrainerSearchPageApiQueryParams>(
    parseUrlQueryParamsForApi(initialQueryFromUrl)
  );
  const [selectedSkillsAndBehaviors, setSelectedSkillsAndBehaviors] =
    useState<SkillsAndBehaviorSlugsDictionary>(() =>
      initialQueryFromUrl
        ? decodeSkillAndBehaviorQueryParams(initialQueryFromUrl)
        : { skillsSlugs: [], behaviorsSlugs: [] }
    );

  const initialEventEmitted = useRef<boolean>(false);

  const [isServiceInterestModalOpen, setIsServiceInterestModalOpen] = useState<boolean>(false);

  const onSearchSuccess = (data: TrainingSearchResponse): void => {
    emitSearchResultsAnalyticsEvent({
      ...data,
      skillsAndBehaviors: {
        skills: selectedSkillsAndBehaviors.skillsSlugs,
        behaviors: selectedSkillsAndBehaviors.behaviorsSlugs,
      },
      usedAutoLocation: !searchQueryForApi.location,
    });
  };

  const {
    data: currentSearchData,
    isLoading,
    error,
    isFetchedAfterMount,
  } = useApiTrainingSearchRetrieve(searchQueryForApi, {
    query: {
      onSuccess: onSearchSuccess,
    },
  });

  const [lastSearchData, setLastSearchData] = useState(currentSearchData);

  // This maintains previous search results/data while loading new results,
  // and updates the URL params (if this is not the initial page load)
  useEffect(() => {
    if (!isLoading && currentSearchData) {
      setLastSearchData(currentSearchData);

      if (isFetchedAfterMount) {
        syncUrlQueryParams(currentSearchData.query, initialQueryFromUrl);
      }
    }
  }, [currentSearchData, isFetchedAfterMount, isLoading, initialQueryFromUrl]);

  useEffect(() => {
    if (currentSearchData?.shouldShowServiceInterestModal) {
      setIsServiceInterestModalOpen(true);
      emitAnalyticsEvent(
        new TrainingSearchServiceInterestLoad({
          searchQueryLocation: currentSearchData.query.location,
        })
      );
    }
  }, [currentSearchData]);

  // Emits the metric for the server-side loaded query after the first client-side render,
  // as that does not cause the query's onSuccess callback to be called
  useEffect(() => {
    if (currentSearchData && initialEventEmitted.current === false && !isFetchedAfterMount) {
      initialEventEmitted.current = true;
      emitSearchResultsAnalyticsEvent({
        ...currentSearchData,
        skillsAndBehaviors: {
          skills: selectedSkillsAndBehaviors.skillsSlugs,
          behaviors: selectedSkillsAndBehaviors.behaviorsSlugs,
        },
        usedAutoLocation: !searchQueryForApi.location,
      });
    }
  }, [
    currentSearchData,
    initialEventEmitted,
    searchQueryForApi,
    selectedSkillsAndBehaviors,
    isFetchedAfterMount,
  ]);

  const updateSkillsAndBehaviors = (skillsAndBehaviors: SkillsAndBehaviorSlugsDictionary): void => {
    const searchData = currentSearchData || lastSearchData;
    if (searchData) {
      emitSearchResultsAnalyticsEvent({
        ...searchData,
        skillsAndBehaviors: {
          skills: skillsAndBehaviors.skillsSlugs,
          behaviors: skillsAndBehaviors.behaviorsSlugs,
        },
        usedAutoLocation: !searchQueryForApi.location,
      });
    }
    setSelectedSkillsAndBehaviors(skillsAndBehaviors);
  };

  const groupedResults: GroupedResults = useMemo(() => {
    return getGroupedResults({
      selectedSkillsAndBehaviors,
      results: currentSearchData?.results ?? lastSearchData?.results ?? [],
    });
  }, [selectedSkillsAndBehaviors, currentSearchData, lastSearchData]);

  const handleFilterSubmit: ComponentProps<typeof FilterSection>['onSubmit'] = useCallback(
    (formData) => {
      emitMetric('training.search.query_submit');
      setSearchQueryForApi(formData); // Will trigger a refetch
    },
    []
  );

  const onMarketingContextHeaderLearnMore = (): void =>
    scrollToFaq(new TrainerSearchHeaderLearnMoreClicked());

  if (error) {
    throw getQueryError(error);
  }

  if (!lastSearchData && isLoading) {
    return <FullPageLoading />;
  }

  return (
    <FloatingFooter.ScrollBoundary offsetTop={`${backToTopStartOffset}px`}>
      <TrainerSearchPageHeader />
      <div
        // TrainerSearchPageLayout can't easily take a ref or an id, so this div
        // serves as the point to which we scroll back up to for the BackToTopButton.
        // We also can't use the FilterSection because it is sticky in mobile.
        ref={topOfPageRef}
      />
      <TrainerSearchPageLayout>
        {shouldDisplayMarketingContextHeader && (
          <Box
            data-testid="mobile-marketing-context-header"
            display={['block', 'none']}
            width="100%"
          >
            <MarketingContextHeader onLearnMore={onMarketingContextHeaderLearnMore} />
          </Box>
        )}
        <FilterSection
          onSubmit={handleFilterSubmit}
          query={currentSearchData?.query || {}}
          selectedSkillsAndBehaviors={selectedSkillsAndBehaviors}
          updateSkillsAndBehaviors={updateSkillsAndBehaviors}
        />
        <SearchResultSection
          // Note, although hard-coded to false, this isMapExpanded prop is passed throughout our
          // training search components for consistency with core search, and the assumption that
          // we will have a map eventually. Consider it unused code for now given our FAQ section
          // that is currently in place of the map.
          isMapExpanded={false}
          results={currentSearchData?.results ?? lastSearchData?.results ?? []}
          groupedResults={groupedResults}
          resultsLoading={isLoading}
          shouldUsePriceTransparency={currentSearchData?.shouldUsePriceTransparency}
          selectedSkillsAndBehaviors={selectedSkillsAndBehaviors}
          searchQuery={currentSearchData?.query ?? lastSearchData?.query ?? {}}
          onMarketingContextHeaderLearnMore={onMarketingContextHeaderLearnMore}
          onFaqPreviewBannerReadMore={() =>
            scrollToFaq(new TrainerSearchFaqPreviewReadMoreClicked())
          }
          shouldDisplayMarketingContextHeader={shouldDisplayMarketingContextHeader}
        />
        <DesktopFaqSection>
          <TrainingFaqs />
        </DesktopFaqSection>
      </TrainerSearchPageLayout>
      <FaqSection>
        <TrainingFaqs
          faqRef={faqRef}
          backToTopStartingElementId={BACK_TO_TOP_STARTING_ELEMENT_ID}
          isFirstQuestionOpen
          // We have two TrainingFaqs rendered on this page at once, and use display none along with
          // breakpoints to ensure only one is accessible to the user. However, this would still emit
          // two schema.org FAQPage scopes to crawlers, which Google flags as an error in the search
          // console. To avoid this, we only include the structured data in the first FAQ rendered,
          // which is recommended by Google if the content is identical.
          // See https://developers.google.com/search/docs/appearance/structured-data/faqpage
          includeMicroData={false}
        />
      </FaqSection>
      <TrainerSearchFooter />
      <BackToTopButton
        onClick={() => scrollToTopOfPage(new TrainerSearchBackToTopButtonClicked())}
        backToTopStartOffset={backToTopStartOffset}
      />
      <ServiceInterestModal
        isOpen={isServiceInterestModalOpen}
        onClose={() => setIsServiceInterestModalOpen(false)}
        searchQueryLocation={currentSearchData?.query.location}
      />
    </FloatingFooter.ScrollBoundary>
  );
}

TrainerSearchPage.reactQueryPrefetch = async (queryClient: QueryClient, req: Request) => {
  const params = parseServerQueryParamsForApi(req.query);

  return queryClient.prefetchQuery({
    queryKey: getApiTrainingSearchRetrieveQueryKey(params),
    queryFn: () => apiTrainingSearchRetrieve(params),
  });
};
TrainerSearchPage.shouldSetBackHistoryHeader = true;

export default TrainerSearchPage;
